// Normalizes a raw document from the /documents/projects/:id API into the
// shape the UI expects. Field names follow the reference tenant-improvement-ui
// repo's `mapDocument()` (utils/documentMapper.ts):
//   document_name -> name
//   approval_status -> status (normalized to "Approved" / "Pending" /
//     "Rejected" so they match the dropdown filter labels)
//   created_at  -> date  (note: created_at, not created_on)
//   category    -> type
//   content_type/original_filename -> fileType (for the row's icon)
// Each field also accepts a camelCase fallback so the function tolerates
// backends that return either case.

const EXTENSION_TO_FILE_TYPE = {
  pdf: 'pdf',
  zip: 'zip',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
}

const CONTENT_TYPE_TO_FILE_TYPE = [
  { match: 'pdf', fileType: 'pdf' },
  { match: 'image/', fileType: 'image' },
  { match: 'zip', fileType: 'zip' },
]

const inferFileTypeFromName = (name) => {
  if (typeof name !== 'string') return ''
  const idx = name.lastIndexOf('.')
  if (idx < 0 || idx === name.length - 1) return ''
  const ext = name.slice(idx + 1).toLowerCase()
  return EXTENSION_TO_FILE_TYPE[ext] || ''
}

const inferFileTypeFromContentType = (contentType) => {
  if (typeof contentType !== 'string' || !contentType) return ''
  const lower = contentType.toLowerCase()
  const hit = CONTENT_TYPE_TO_FILE_TYPE.find(({ match }) => lower.includes(match))
  return hit ? hit.fileType : ''
}

const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) return value
  }
  return ''
}

// Backend ships approval_status as lowercase "pending" / "approved" /
// "rejected". The Documents tab UI (badges, dropdown filter) speaks the
// title-case strings. Centralise the mapping here so every consumer agrees.
const STATUS_LABEL_BY_API_VALUE = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
}

const normalizeStatus = (value) => {
  if (typeof value !== 'string' || !value) return ''
  const lower = value.toLowerCase()
  if (STATUS_LABEL_BY_API_VALUE[lower]) return STATUS_LABEL_BY_API_VALUE[lower]
  // Already title-cased ("Approved") or unknown — pass through verbatim so
  // unrecognised statuses still render and surface to the user.
  return value
}

export const transformDocument = (raw) => {
  if (!raw || typeof raw !== 'object') return null

  // The reference repo prefers `document_name` over `name` because the DB
  // column is document_name; the original filename is stored separately.
  const name = firstString(
    raw.document_name,
    raw.documentName,
    raw.name,
    raw.file_name,
    raw.fileName,
    raw.title,
  )
  const originalFilename = firstString(
    raw.original_filename,
    raw.originalFilename,
  )

  const contentType = firstString(raw.content_type, raw.contentType, raw.mime_type)

  const fileType =
    firstString(raw.fileType, raw.file_type) ||
    inferFileTypeFromContentType(contentType) ||
    inferFileTypeFromName(originalFilename) ||
    inferFileTypeFromName(name)

  const rawStatus = firstString(
    raw.approval_status,
    raw.approvalStatus,
    raw.status,
    raw.review_status,
  )

  return {
    id: raw.id != null ? String(raw.id) : '',
    // Fall back to originalFilename so the row's primary cell is never
    // blank — an empty primary string makes the row invisible to
    // keyboard / screen-reader users (their accessible name comes from
    // this string) and renders as a layout bug to sighted users.
    // Mirrors the reference tenant-improvement-ui repo's behaviour.
    name: name || originalFilename,
    // The API does not currently return a sub-label, but several earlier
    // mocks did; we keep the field for back-compat with any consumer that
    // still wants to surface a "phase" / "section" beneath the name.
    subLabel: firstString(raw.subLabel, raw.sub_label, raw.phase, raw.section),
    type: firstString(raw.category, raw.type, raw.document_type),
    status: normalizeStatus(rawStatus),
    date: firstString(
      raw.created_at,
      raw.createdAt,
      raw.created_on,
      raw.createdOn,
      raw.uploaded_on,
      raw.uploadedOn,
      raw.date,
    ),
    fileType,
    // We deliberately drop the legacy `url` field. With auth-protected
    // /view + /download endpoints, the cell uses the document id and an
    // axios blob fetch instead of <a href>.
    originalFilename,
  }
}
